import BusinessIcon from '@mui/icons-material/Business'
import CropFreeIcon from '@mui/icons-material/CropFree'
import PersonIcon from '@mui/icons-material/Person'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CardFactsStrip, {
  FactChip,
  SignalChip
} from 'src/components/LocationSearchCard/CardFactsStrip'
import SearchCardWhatsappButton, {
  SearchCardWhatsappDealType
} from 'src/components/LocationSearchCard/SearchCardWhatsappButton'
import PromoBadge, { promoBadgeContent } from 'src/components/Promo/PromoBadge'
import { useAdminHostBadge } from 'src/components/SearchManager/AdminHostBadges'
import { haversineKm } from 'src/constants/onDemandCoverage'
import { isVehicleLocationType } from 'src/constants/pillarBoundaries'
import { useFilterProvider } from 'src/context/FilterProvider'
import useBreakpoints from 'src/hooks/useBreakpoints'
import useCardPrice from 'src/hooks/useCardPrice'
import useCommon from 'src/hooks/useCommon'
import useConfigSummary from 'src/hooks/useConfigSummary'
import useInView from 'src/hooks/useInView'
import { DealType, ILocation } from 'src/types/graphql'
import { afterNextPaint, runAfterNextPaint } from 'src/utils/afterNextPaint'
import { getDiscount } from 'src/utils/coverPhoto'
import { operatorPromoPill } from 'src/utils/operatorPromo'
import {
  trackCarouselAdvance,
  trackSearchResultClick,
  trackSearchResultView
} from 'src/utils/pixels/searchEvents'
import { getEffectiveDealType } from 'src/utils/pricing'
import {
  buildCardBadges,
  formatCardDistance,
  getCardDemand,
  getCardScarcity,
  resolveSearchZone
} from 'src/utils/searchAnalytics'
import { numberRounedUpWithoutDecimals } from '../../utils'
import ImageCarousel from '../ImageCarousel'
import VerifiedBadge from '../VerifiedBadge'

interface SpotCardProps {
  location: ILocation
  toggleLocation?: (location: ILocation | undefined) => void
  hidePrice?: boolean
  priority?: boolean
  /** Analytics surface this card renders in. Defaults to the list. */
  surface?: 'list_card'
  /** 1-based rank in the result list, for surface-aware CTR. */
  position?: number
}

const SpotCard = ({
  location,
  toggleLocation,
  hidePrice,
  priority,
  surface = 'list_card',
  position
}: SpotCardProps) => {
  const { t } = useTranslation('common')
  const { isUpMd, isDownMd } = useBreakpoints()
  // `cardLocation` applies the admin branch reference and normalizes the
  // municipality from the postal code. `shortAddress` is the fallback for any
  // caller whose query predates the field.
  const { defaultConfiguration, shortAddress, cardLocation } = location
  const locationLabel = cardLocation || shortAddress
  const { urlRoute, canStoreVehicle, locationType } = defaultConfiguration ?? {}
  // `locationType` first: the shared CARD fragment deliberately trimmed
  // `canStoreVehicle` from `defaultConfiguration` (fragments.ts), so on every
  // search/recs surface it's undefined and a bare `canStoreVehicle === true`
  // never fires — the parking pill branch was dead on card data (found in
  // the 2026-09-02 browser pass). `isVehicleLocationType` is the canonical
  // vehicle check (cross-repo rule — never hand-write this test).
  const isParking =
    isVehicleLocationType(locationType) || canStoreVehicle === true
  const {
    hasMultipleConfigurations,
    numConfigurations,
    sizeRangeText,
    totalAvailability
  } = useConfigSummary(location)
  // Parking pill shows AVAILABLE units, not config count — the summary sum
  // covers multi-config listings; `defaultConfiguration.availability` is the
  // fallback for shapes without `configSummary.totalAvailability` (thin/legacy
  // queries, and ISR pages prerendered before the field shipped — the schema
  // says `Int!`, but a stale static payload simply lacks the key). Don't
  // "simplify" the `??` chain away.
  const availableSpots = isParking
    ? (totalAvailability ?? defaultConfiguration?.availability ?? null)
    : null
  // Admin-only landlord label (sales team). Populated only under the search
  // surface's AdminHostBadgesProvider AND only for admin viewers — renters
  // never receive host identity on cards.
  const adminHostBadge = useAdminHostBadge(location.id)
  const { locMap, showSize } = useCommon()
  const locType = locMap(location)
  const { filters, shallowParams, isReady, isSliderOpen } = useFilterProvider()

  const getUrl = (args?: { [key: string]: string }) => {
    const entries = Object.entries({
      ...shallowParams,
      ...filters,
      ...args,
      sliderOpen: isSliderOpen
    }).filter(([, v]) => v !== undefined && v !== null)
    const params = new URLSearchParams(entries as string[][])
    const qs = params.toString()
    return qs ? `${urlRoute ?? ''}?${qs}` : (urlRoute ?? '')
  }

  const [isClicked, setIsClicked] = React.useState(false)
  // Hydration gate for the breakpoint-dependent <Link> shell below.
  // useBreakpoints (MUI useMediaQuery → useSyncExternalStore) returns the
  // SSR default (false) on the server but the LIVE matchMedia value on the
  // client's first render. On mobile that means SSR emits the desktop
  // anchor (target=_blank, block h-full) while the client's first paint
  // emits the mobile anchor (block, no target) → React 19 #418 on every
  // mobile search-results pageview. Gating the breakpoint divergence behind
  // `mounted` keeps SSR and first client paint identical (both desktop
  // shape); the mobile/desktop behavior swaps in after hydration.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  // Scroll/history persistence + Router.push for mobile cards. PostHog
  // session-replay showed 84% of listing-card sessions dead-clicking on
  // mobile during the first ~60s after page load — root cause: the
  // mobile card was a `<div onClick>` with no anchor, so taps before
  // React hydrated (or before the async chain below resolved) did
  // nothing. Mobile path is now wrapped in a `<Link>` (see render
  // below) so the native anchor href navigates pre-hydration; this
  // function runs when hydration has caught up and preserves
  // scroll/drawer state for the back-nav restore.
  // One mobile tap reaches doNavigate TWICE — the card's root `<div onClick>`
  // and the `<Link>` onClick both call it — which ran two shallow replaces
  // and two pushes per tap (the second cancelling the first). Re-entry guard;
  // it resets once the push settles so a later tap on this card still works.
  const isNavigatingRef = useRef(false)
  const doNavigate = async () => {
    if (isNavigatingRef.current) return
    isNavigatingRef.current = true
    try {
      // Set isClicked FIRST so the scroll-restoration effect's guard
      // (`if (isClicked) return` in the useEffect below) suppresses an
      // outbound scrollIntoView. Without this, the Router.replace below
      // updates `shallowParams.scrollY` mid-flight, the effect fires with
      // `isClicked` still false on the matching card, and we re-center
      // the just-tapped card a few frames before navigating — the visible
      // flash. Return-nav still restores scroll because /search remounts
      // and `isClicked` resets to false.
      setIsClicked(true)
      // Persist the drawer + scroll context into the search page's history
      // entry so `router.back()` from the listing restores both the open
      // drawer state and the scroll position to the clicked card.
      //
      // Why Router.replace and not window.history.replaceState:
      // Next.js stores its own `as`/`url` inside `window.history.state`. A
      // raw `history.replaceState` updates the URL bar but Next still uses
      // its internal copy, so on back navigation the page rehydrates from
      // the pre-replace URL and `sliderOpen`/`scrollY` are lost. Awaiting
      // Router.replace ensures Next's state is written before we Router.push
      // the listing on top of it.
      if (typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href)
        const sliderDesired = isSliderOpen ? 'true' : 'false'
        let dirty = false
        if (currentUrl.searchParams.get('sliderOpen') !== sliderDesired) {
          currentUrl.searchParams.set('sliderOpen', sliderDesired)
          dirty = true
        }
        if (currentUrl.searchParams.get('scrollY') !== location.id) {
          currentUrl.searchParams.set('scrollY', location.id)
          dirty = true
        }
        if (dirty) {
          // Let the tap paint first. This replace is pure history
          // bookkeeping, and `history.replaceState` is wrapped by Meta /
          // Bing / LinkedIn (each fires its own SPA page hook): 156 ms at
          // 4× CPU inside the tap before this yield
          // (docs/performance/interaction-latency.md).
          await afterNextPaint()
          await Router.replace(
            `${currentUrl.pathname}${currentUrl.search}`,
            undefined,
            { shallow: true, scroll: false }
          )
        }
      }
      const url = getUrl({ scrollY: location.id })
      await Router.push(url ?? '')
    } finally {
      isNavigatingRef.current = false
    }
  }

  // Mobile <Link> click handler — lets the browser handle modifier
  // clicks (cmd/ctrl/shift/middle-click) natively, otherwise blocks
  // the native nav so doNavigate() can persist scroll/drawer state and
  // push via the router. When JS hasn't hydrated yet this handler
  // doesn't run and the native href takes over — that's the whole
  // point of the rewrite.
  const onMobileLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isUpMd) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return
    }
    e.preventDefault()
    void doNavigate()
  }

  // Legacy `onClick` for the inner div / ImageCarousel. With the
  // <Link> wrapper, clicks bubble up to the Link's handler — but
  // ImageCarousel's prev/next buttons call e.stopPropagation(), so
  // image swipe clicks would skip the Link entirely. Keep this thin
  // wrapper around `doNavigate` so swipe-end taps still navigate.
  const onClick = () => {
    if (isUpMd) return
    void doNavigate()
  }

  // Stable identity so ImageCarousel's Embla `select` listener isn't
  // re-subscribed on every render. Dedup (once/session/config) lives in the
  // helper.
  const handleCarouselAdvance = useCallback(() => {
    if (defaultConfiguration?.id) {
      trackCarouselAdvance({ configId: defaultConfiguration.id, surface })
    }
  }, [defaultConfiguration?.id, surface])

  const url = getUrl()
  const sizeText = sizeRangeText ?? showSize(location, true)

  const { price, isBrokerMode, showFromPrice } = useCardPrice(location)
  const discount = getDiscount(defaultConfiguration, isBrokerMode)
  // The Traditional operator's OWN promotion (never a SpotMe discount, so it
  // never co-occurs with `discount`, which is SpotMe Now only).
  const promoPill = operatorPromoPill(defaultConfiguration?.operatorPromo)
  const promoBadge = promoBadgeContent(discount, promoPill)
  // Off-platform cards (Traditional / Broker) get the WhatsApp pill; SpotMe
  // Now cards stay as they are (their product is the listing + Reservar).
  // Resolved from `resolvedDealType` (CARD fragment) via the canonical
  // helper — never raw `dealType`. Data-only, so SSR == first paint.
  const effectiveDealType = defaultConfiguration
    ? getEffectiveDealType(defaultConfiguration)
    : null
  const waDealType: SearchCardWhatsappDealType | null =
    effectiveDealType === DealType.Traditional ||
    effectiveDealType === DealType.Broker
      ? effectiveDealType
      : null

  const ref = React.useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isReady || !isDownMd) return
    if (!isSliderOpen) return
    if (!ref.current) return
    if (isClicked) return

    const isInViewWindow = ref.current.getBoundingClientRect().top < 0
    if (isInViewWindow) return

    if (shallowParams.scrollY === location.id) {
      ref.current.scrollIntoView({ behavior: 'instant', block: 'center' })
    }
  }, [isClicked, isDownMd, isReady, location.id, shallowParams, isSliderOpen])

  // Viewed-CTR denominator: fire search_result_view once when this card first
  // reaches ≥50% visibility. Reuses the card-root `ref` (read-only here vs the
  // scroll-restoration effect above); useInView disconnects after first
  // intersection, and trackSearchResultView dedups per session+config.
  const inView = useInView(ref, { threshold: 0.5 })
  // The zone this impression was served in — the pre/post dimension the
  // directory dilution guardrail slices on (plan §4.2). Read from the search
  // context, not the listing's address: the tier floor is switched on per
  // search zone, so that is the unit the treatment applies to.
  //
  // `useRouter()`, NOT the `next/router` singleton: the singleton's `.query`
  // getter THROWS during SSR ("No router instance found"), and this runs in
  // the render body of a card that server-renders on every pillar/city/hood
  // request — reading it here 500'd the site's main organic + paid landing
  // surface. Memoized because this component mounts up to ~50 times per
  // search page.
  const router = useRouter()
  const zone = useMemo(
    () => resolveSearchZone(router.query?.city, filters.city),
    [router.query?.city, filters.city]
  )
  // What this card rendered — rides both the view and the click so badge
  // lift reads as click/view per badge (see SearchCardBadges).
  const cardBadges = useMemo(
    () => buildCardBadges(location, isBrokerMode),
    [location, isBrokerMode]
  )
  useEffect(() => {
    if (!inView) return
    trackSearchResultView({
      configId: defaultConfiguration?.id,
      locationId: location.id,
      position,
      zone,
      coverPhotoId: location.coverPhoto?.id,
      badges: cardBadges
    })
  }, [
    inView,
    defaultConfiguration?.id,
    location.id,
    position,
    zone,
    location.coverPhoto?.id,
    cardBadges
  ])

  const [isHovered, setIsHovered] = useState(false)
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null)

  // Helper to handle hover in/out for overlay and buttons
  const handleMouseEnter = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
    setIsHovered(true)
    if (isUpMd) toggleLocation?.(location)
  }, [isUpMd, location, toggleLocation])

  const handleMouseLeave = useCallback(
    (buttons?: boolean) => () => {
      if (buttons) return
      // Small delay to allow moving between overlay and buttons
      hoverTimeout.current = setTimeout(() => setIsHovered(false), 40)
      if (isUpMd) toggleLocation?.(undefined)
    },
    [isUpMd, toggleLocation]
  )

  const [iconBig, value, unit] = isBrokerMode
    ? [
        <CropFreeIcon className="mt-[-2.5px] mr-1 text-lg" key="iconBig" />,
        price,
        '/m²'
      ]
    : [null, price, `/${t('FILTER.MONTH')}`]

  // Urgency pill copy. Source-of-truth lives in `getCardScarcity` which
  // bakes in card-specific exclusions: no broker (bodegas comerciales /
  // naves industriales), no parking, and watching-count is intentionally
  // dropped — small watcher numbers read as weak / inflated on a card
  // grid. The helper returns `null` when no urgency applies so the pill
  // node only mounts on a real scarcity signal.
  const urgencyLabel = useMemo(() => {
    if (!defaultConfiguration) return null
    const scarcity = getCardScarcity(
      defaultConfiguration,
      isBrokerMode,
      location.inventoryIsFresh
    )
    if (!scarcity) return null
    return scarcity.kind === 'last_one'
      ? t('SEARCH_CARD.URGENCY_LAST_ONE')
      : t('SEARCH_CARD.URGENCY_FEW_LEFT', { count: scarcity.count })
  }, [defaultConfiguration, isBrokerMode, location.inventoryIsFresh, t])

  // Branch-level demand («N consultas en 30 días») — real distinct people,
  // server-floored at 3; yields to the scarcity pill (getCardDemand).
  const demandCount = getCardDemand(location, urgencyLabel !== null)

  // Distance from the user's search center to this listing. Only renders
  // when the search carries an explicit lat/lng (filters set, map-pan, or
  // city/hood pages) AND the listing has coordinates. /search without a
  // location yields no distance — accurate, since there's no anchor.
  const { lat: searchLat, lng: searchLng } = filters
  const locLat = location.coordinates?.lat
  const locLng = location.coordinates?.lng
  const distanceLabel = useMemo(() => {
    if (
      typeof searchLat !== 'number' ||
      typeof searchLng !== 'number' ||
      typeof locLat !== 'number' ||
      typeof locLng !== 'number'
    ) {
      return null
    }
    const km = haversineKm(searchLat, searchLng, locLat, locLng)
    const { key, value } = formatCardDistance(km)
    return key === 'DISTANCE_METERS'
      ? t('SEARCH_CARD.DISTANCE_METERS', { meters: value })
      : t('SEARCH_CARD.DISTANCE_KM', { km: value })
  }, [searchLat, searchLng, locLat, locLng, t])

  // Built once, placed per layout: the facts footer on the photo-left card,
  // the photo + copy column on the stacked card (`search_card_stacked`).
  const sizeChip =
    isParking && !hasMultipleConfigurations && availableSpots == null ? null : (
      <FactChip>
        {isParking ? (
          <span className="font-semibold text-blue-500">
            {availableSpots != null
              ? t('SEARCH_CARD.AVAILABLE_SPOTS', { count: availableSpots })
              : t('SEARCH_CARD.SPOTS', { count: numConfigurations })}
          </span>
        ) : (
          <>
            {hasMultipleConfigurations && (
              <span className="font-semibold text-blue-500">
                {numConfigurations}
                <span className="mx-0.5 text-ink-200">·</span>
              </span>
            )}
            {sizeText}
          </>
        )}
      </FactChip>
    )
  const demandChip =
    demandCount !== null ? (
      <SignalChip>
        {t('SEARCH_CARD.DEMAND_30D', { count: demandCount })}
      </SignalChip>
    ) : null

  const cardContent = (
    <div
      onClick={onClick}
      ref={ref}
      className={`${'location-search-card'} group relative overflow-hidden rounded-2xl border border-ink-100/15 bg-white-500 transition-all duration-300`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave()}
    >
      {/* FIXED 142/160px card — every card in the list is the same height,
          discount or not. A `min-h` variant that let discounted cards grow
          (stacking the offer under the price) shipped on 2026-09-25 and made
          the list ragged (142 vs 155–159px on /renta-mini-bodegas/cdmx).
          Every card has the facts footer, so the rhythm stays uniform. */}
      <div className="flex h-[142px] flex-col md:h-[160px] card-stacked:h-auto">
        <div className="flex min-h-0 flex-1 card-stacked:flex-col">
          {/* Image Section — only the short urgency pill («Última», «Quedan
            N») sits on the photo, top-left, clear of the carousel dots. The
            promo badge and the demand count moved to the footer: on the
            130px photo they broke into two lines (2026-10-28). z-10 +
            pointer-events-none so swipe still works underneath. */}
          {/* 200px at `lg:`, not `md:`: the rail is 45% of the viewport, so at
            900–1023px it is only ~357–412px and a 200px image starves the
            copy column. Kept in lockstep with the cross-link tiles +
            `SearchLoader` so the grid shares one image rhythm. */}
          <div className="relative w-[130px] shrink-0 overflow-hidden sm:w-[155px] lg:w-[200px] card-stacked:aspect-[16/9] card-stacked:w-full">
            <div className="absolute inset-0">
              <ImageCarousel
                location={location}
                className="size-full object-cover"
                isHovered={isHovered}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                onClick={onClick}
                priority={priority}
                horizontal
                onAdvance={handleCarouselAdvance}
              />
            </div>
            {(urgencyLabel || promoBadge) && (
              <div className="pointer-events-none absolute top-2 right-2 left-2 z-10 flex flex-col items-start gap-1">
                {/* Stacked card only: the full-width photo holds the badge on
                    one line, the way the home / similar cards do. */}
                {promoBadge && (
                  <span className="hidden max-w-full card-stacked:flex">
                    <PromoBadge promo={promoBadge} placement="overlay" />
                  </span>
                )}
                {urgencyLabel && (
                  <span className="rounded-full bg-ink-700/85 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap text-white-500 backdrop-blur-sm md:text-[11px]">
                    {urgencyLabel}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Content Section — `min-w-0` is load-bearing, not decoration. A flex
            item defaults to `min-width: auto` (= min-content), so without it
            this column refuses to shrink below its longest word and the inner
            `truncate` never fires: long labels blew the column ~49px past the
            card at 320px and got hard-clipped by the card's `overflow-hidden`,
            ellipsis and all. Measured on prod 2026-07-31 with
            "Tlajomulco de Zúñiga, Jal." — 187px of text in a ~138px slot. */}
          <div className="flex min-w-0 flex-1 flex-col justify-between p-3 md:px-4 md:py-3 card-stacked:justify-start card-stacked:gap-2">
            {/* Header — title gets the full row width on mobile too. The
              size pill moved down to the rating row so multi-config ranges
              like `3 · 150 – 300 m²` can't push the type into ellipsis. */}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-sm font-semibold tracking-[-0.005em] text-ink-700 md:text-base">
                  {locType ? `${locType}` : ''}
                </p>
                <VerifiedBadge
                  verified={location.isVerified}
                  variant="compact"
                  className="shrink-0"
                />
              </div>
              {/* Label and distance are SIBLINGS, not one truncated string. When
                they shared a `<p className="truncate">` the distance sat at the
                end, so it was the first thing the ellipsis ate — the card lost
                the proximity signal precisely on the long labels that need it
                most. `shrink-0` pins the distance; the label absorbs the
                overflow and ellipsises on its own. */}
              <div className="mt-0.5 flex items-baseline gap-1 text-xs text-ink-200 md:text-sm">
                <span className="truncate">{locationLabel}</span>
                {distanceLabel && (
                  <span className="shrink-0 whitespace-nowrap">
                    {`· ${distanceLabel}`}
                  </span>
                )}
              </div>
              {/* Admin-only landlord chip (sales team). In the COPY column, not
                over the photo: the photo is 130px wide and its overlay space is
                already taken — urgency pill top-left, the carousel's own
                pagination dots at `bottom-2` on `z-20` (they paint over any
                card-level overlay), and the hover nav arrows on both edges at
                `top-1/2`. Here it gets ~2x the width for long operator names,
                truncating with a `title` tooltip for the full string. */}
              {adminHostBadge && (
                <div
                  title={adminHostBadge.name}
                  className="mt-1 flex w-fit max-w-full items-center gap-1 rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[10px] leading-tight font-semibold text-blue-800 md:text-[11px]"
                >
                  {adminHostBadge.isTeam ? (
                    <BusinessIcon className="size-3 shrink-0" />
                  ) : (
                    <PersonIcon className="size-3 shrink-0" />
                  )}
                  <span className="truncate">{adminHostBadge.name}</span>
                </div>
              )}
            </div>

            {/* Stacked card only: size + demand under the location (the
                footer is hidden there). One line; a chip that doesn't fit
                drops whole, as in CardFactsStrip. */}
            {/* Rendered even when empty so every stacked card is one height. */}
            <div className="hidden h-5 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-6 overflow-hidden card-stacked:flex">
              {sizeChip}
              {demandChip}
            </div>

            {/* Price on one line — the discount overlays the photo, so it
              never squeezes the price at 320px (a 53px-wide, 104px-tall pill
              was the failure mode inline) and never changes the row height.
              Traditional/Broker cards add the WhatsApp pill at the row's end:
              a 28px circle on mobile (~100px left for the amount at 320),
              labelled only from `xl:`, so it never pushes the price. */}
            <div className="flex items-center justify-between gap-1.5 md:gap-2 card-stacked:min-h-9">
              <span className="min-w-0 text-blue-500">
                {/* «Desde» is always its own label line: inline, it fit at
                    some widths and wrapped at others (320, 900–1024px), so
                    the same card changed shape across breakpoints. */}
                {showFromPrice && (
                  // Inline on the stacked card (full-width row, room to spare)
                  // so «Desde» never adds a line there.
                  <span className="block text-xs leading-tight font-medium text-ink-200 md:text-sm card-stacked:mr-1 card-stacked:inline">
                    {t('SEARCH_CARD.FROM_PRICE')}
                  </span>
                )}
                {/* Amount + unit never split ("$12,000" / "/mes"). */}
                {/* `data-card-amount` is read by SearchCardWhatsappButton's fit
                  check — the pill hides itself when this span would run
                  under it (long prices at 320px and 900–1279px). */}
                <span data-card-amount className="whitespace-nowrap">
                  {iconBig}
                  <span className="font-display text-lg font-medium tracking-[-0.01em] italic md:text-2xl">
                    {`$${hidePrice ? 'X' : numberRounedUpWithoutDecimals(value)}`}
                  </span>
                  <span className="text-sm font-medium text-ink-200 md:text-base">
                    {unit}
                  </span>
                </span>
              </span>
              {waDealType && (
                <SearchCardWhatsappButton
                  location={location}
                  dealType={waDealType}
                  position={position}
                  zone={zone}
                />
              )}
            </div>
          </div>
        </div>
        {/* Promo first: it is the one fact a short row must never drop. The
            star score that once shared the size row was removed 2026-09-30
            (typed by an admin, not computed from stored reviews). */}
        <CardFactsStrip className="card-stacked:hidden">
          {promoBadge && <PromoBadge promo={promoBadge} placement="inline" />}
          {/* Parking: AVAILABLE units ("N lugares") when known — single- and
              multi-config alike; config-count fallback for thin shapes;
              single-config with no availability data shows no chip.
              Otherwise (storage) size + count. */}
          {sizeChip}
          {demandChip}
        </CardFactsStrip>
      </div>
    </div>
  )

  // Single <Link> shell, deterministic on SSR + first client paint.
  // Pre-mount (server + first paint): desktop shape — matches SSR's
  // isDownMd=false so hydration never diverges. The native href is present
  // either way, so a tap during the pre-hydration window still navigates
  // (same-tab) on every device.
  // Post-mount:
  //   - mobile → same-tab nav + onMobileLinkClick (persists scroll/drawer
  //     state, see doNavigate); no target=_blank.
  //   - desktop → new tab via target=_blank + rel, no onClick.
  const isMobile = mounted && isDownMd
  return (
    <Link
      href={url}
      onClick={(e) => {
        // Surface attribution fires on EVERY click (desktop new-tab + mobile
        // tap); side-effect only (no preventDefault). Mobile additionally runs
        // onMobileLinkClick for scroll/drawer persistence. Fired after the
        // tap paints: it is a discovery event (not a conversion) and gtag
        // cost 105 ms at 4× CPU inside the tap. A new tab hides this page,
        // which flushes it immediately (see afterNextPaint).
        const clickArgs = {
          surface,
          configId: defaultConfiguration?.id,
          locationId: location.id,
          position,
          zone,
          coverPhotoId: location.coverPhoto?.id,
          badges: cardBadges
        }
        runAfterNextPaint(() => trackSearchResultClick(clickArgs))
        if (isMobile) onMobileLinkClick(e)
      }}
      target={mounted && !isMobile ? '_blank' : undefined}
      rel={mounted && !isMobile ? 'noopener noreferrer' : undefined}
      className={isMobile ? 'block' : 'block h-full'}
    >
      {cardContent}
    </Link>
  )
}

export default React.memo(SpotCard)
